import type { FC } from 'react';

import { useCms, useContent } from '@eneco/cms';
import { linkWithUrl } from '@eneco/cms-types';
import RichText from '@eneco/components/RichText/RichText';
import { Bucket as SparkyBucket, TextLink } from '@eneco/sparky';

import type { BucketContent } from './bucket-content.types';

/**
 * `Bucket` block for the CMS-agnostic lane, serving **both** providers.
 *
 * Mirrors `libs/sitecore/components/content/src/Bucket/Bucket.tsx` 1:1: a Sparky `Bucket` titled from
 * `title`, whose content is the rich text followed by an optional high-emphasis `TextLink`.
 *
 * Drops the legacy `wrap()`/Experience Editor `editable` checks — this lane never runs inside the
 * Experience Editor — and the legacy `useLinkComponent()` in favour of `linkWithUrl()` plus a plain
 * `TextLink`, matching every other migrated component in this lane (see `LinkCard.tsx`).
 *
 * Sparky's `Bucket` requires a `title`; the Sitecore field is always present, but the Sanity schema
 * marks it optional, so an absent one falls back to `''` — the same empty heading Sitecore renders for
 * an empty title field, rather than dropping the block.
 *
 * A link whose URL could not be built is left out rather than pointed at the wrong place.
 */
const Bucket: FC = () => {
  const { adapter } = useCms();
  const content = useContent<BucketContent>();

  const richText = adapter.toRichText(content.content);
  const link = linkWithUrl(content.link);

  return (
    <SparkyBucket title={content.title ?? ''}>
      <SparkyBucket.Content>
        {richText && <RichText html={richText} />}

        {link?.href && (
          <TextLink href={link.href} emphasis="high" target={link.target}>
            {link.text}
          </TextLink>
        )}
      </SparkyBucket.Content>
    </SparkyBucket>
  );
};

export default Bucket;
